import React, { Component } from 'react';
import classNames from 'classnames';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { ensureListing } from '../../../util/data';
import { isPriceVariationsEnabled } from '../../../util/configHelpers';

import css from './SearchMapPriceLabel.module.css';

/**
 * SearchMapPriceLabel component
 *
 * The Hotline Glam browse map uses the artist's first listing image above a compact
 * navy price pill. If no image is available, the price pill still renders normally.
 *
 * @component
 */
class SearchMapPriceLabel extends Component {
  shouldComponentUpdate(nextProps) {
    const currentListing = ensureListing(this.props.listing);
    const nextListing = ensureListing(nextProps.listing);
    const isSameListing = currentListing.id.uuid === nextListing.id.uuid;
    const hasSamePrice = currentListing.attributes.price === nextListing.attributes.price;
    const hasSameActiveStatus = this.props.isActive === nextProps.isActive;
    const hasSameRefreshToken =
      this.props.mapComponentRefreshToken === nextProps.mapComponentRefreshToken;

    return !(isSameListing && hasSamePrice && hasSameActiveStatus && hasSameRefreshToken);
  }

  render() {
    const {
      className,
      rootClassName,
      listing,
      onListingClicked,
      intl,
      isActive,
      config,
    } = this.props;
    const currentListing = ensureListing(listing);
    const { price, publicData } = currentListing.attributes;

    const formattedPrice =
      price && price.currency === config.currency
        ? formatMoney(intl, price)
        : price?.currency
        ? price.currency
        : null;

    const priceValue = formattedPrice
      ? intl.formatMessage({ id: 'SearchMapPriceLabel.price' }, { priceValue: formattedPrice })
      : null;

    const validListingTypes = config.listing.listingTypes;
    const foundListingTypeConfig = validListingTypes.find(
      conf => conf.listingType === publicData?.listingType
    );
    const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
    const hasMultiplePriceVariants =
      isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

    const priceMessage = hasMultiplePriceVariants
      ? intl.formatMessage({ id: 'SearchMapInfoCard.priceStartingFrom' }, { priceValue })
      : intl.formatMessage({ id: 'SearchMapInfoCard.price' }, { priceValue });

    const markerPriceValue = price
      ? intl.formatNumber(price.amount / 100, {
          style: 'currency',
          currency: price.currency,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: price.amount % 100 === 0 ? 0 : 2,
          maximumFractionDigits: 2,
        })
      : null;

    const firstImage = currentListing?.images?.[0] || null;
    const imageVariants = firstImage?.attributes?.variants || {};
    const preferredImageVariant =
      Object.entries(imageVariants)
        .filter(([name, variant]) => name.startsWith('listing-card') && variant?.url)
        .sort(([, a], [, b]) => (a?.width || 0) - (b?.width || 0))[0]?.[1] ||
      Object.values(imageVariants).find(variant => variant?.url) ||
      null;
    const imageUrl = preferredImageVariant?.url || null;

    const classes = classNames(rootClassName || css.root, className, {
      [css.rootActive]: isActive,
    });
    const priceLabelClasses = classNames(css.priceLabel, {
      [css.mapLabelActive]: isActive,
      [css.noPriceSetLabel]: !markerPriceValue,
    });

    const ariaLabel = priceValue
      ? priceMessage
      : intl.formatMessage({ id: 'SearchMapPriceLabel.screenreader.mapMarkerWithoutPrice' });

    return (
      <button
        className={classes}
        onClick={() => onListingClicked(currentListing)}
        aria-label={ariaLabel}
      >
        {imageUrl ? (
          <span className={css.artistPhotoWrap} aria-hidden="true">
            <img className={css.artistPhoto} src={imageUrl} alt="" />
          </span>
        ) : null}
        <span className={priceLabelClasses}>{markerPriceValue || '•'}</span>
      </button>
    );
  }
}

SearchMapPriceLabel.propTypes = {
  listing: propTypes.listing.isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(SearchMapPriceLabel);
